import React, { useState } from "react";
import { Container, Paper, Box, Typography, TextField, Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { getInvoicesList } from "../DatabaseApi/DatabaseApi1";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase.config";
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const safeNumber = (v) => {
  if (v === undefined || v === null) return 0;
  const n = parseFloat(String(v).replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const formatDateDMY = (dateInput) => {
  if (!dateInput) return '';
  let d;
  try {
    // Firestore timestamp support
    if (dateInput && typeof dateInput.toDate === 'function') {
      d = dateInput.toDate();
    } else {
      d = new Date(dateInput);
    }
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const mon = months[d.getMonth()] || String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mon}-${yyyy}`;
  } catch (e) {
    return '';
  }
};

const parseQuotation = (q) => {
  try {
    const obj = typeof q === "string" ? JSON.parse(q) : q;
    return obj || {};
  } catch (e) {
    return q || {};
  }
};

export default function TaxReport() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // Compute allowable date range: past 3 years up to today
  const formatISODate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const today = new Date();
  const maxAllowedDate = formatISODate(today);
  const minDateObj = new Date(today);
  minDateObj.setFullYear(minDateObj.getFullYear() - 3);
  const minAllowedDate = formatISODate(minDateObj);

  const clampDateToRange = (val) => {
    if (!val) return val;
    if (val < minAllowedDate) return minAllowedDate;
    if (val > maxAllowedDate) return maxAllowedDate;
    return val;
  };

  const handleStartDateChange = (val) => {
    setStartDate(clampDateToRange(val));
  };

  const handleEndDateChange = (val) => {
    setEndDate(clampDateToRange(val));
  };

  const inRange = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    if (startDate) {
      const s = new Date(startDate);
      if (d < s) return false;
    }
    if (endDate) {
      const e = new Date(endDate);
      // include whole day
      e.setHours(23, 59, 59, 999);
      if (d > e) return false;
    }
    return true;
  };

  const generate = async () => {
    setLoading(true);
    try {
      // Fetch only tax invoices (exclude proforma)
      const taxInvoices = await getInvoicesList("tax");

      // Fetch expenses collection
      const expenseSnap = await getDocs(collection(db, "expense"));
      const expenseDocs = [];
      expenseSnap.forEach((d) => expenseDocs.push({ id: d.id, ...d.data() }));

      const initial = {
        invoiceCount: 0,
        expenseCount: 0,
        taxableAmount: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalAmount: 0,
      };

      const agg = { ...initial };

      const deriveTaxableBase = (providedBase, totalWithGST, cgst, sgst, igst) => {
        // If a taxable base was explicitly provided (including negative values), use it.
        if (providedBase !== undefined && providedBase !== null && String(providedBase).toString().trim() !== "") {
          return safeNumber(providedBase);
        }

        // Otherwise try to derive from total-with-gst minus sum of tax components.
        const t = safeNumber(totalWithGST);
        const taxSum = safeNumber(cgst) + safeNumber(sgst) + safeNumber(igst);

        // If total-with-gst is provided (could be negative for credit/returns), preserve its sign.
        if (t !== 0) return t - taxSum;

        return 0;
      };

      const entries = [];

      // Process tax invoices (these should reduce the tax liabilities): treat as negative values
      (taxInvoices || []).forEach((inv) => {
        const date = inv.invoiceDate || inv.date || inv.invoice_date || null;
        if (startDate || endDate) {
          if (!inRange(date)) return;
        }

        const cgst = safeNumber(inv.cgstAmount || inv.cgst || 0);
        const sgst = safeNumber(inv.sgstAmount || inv.sgst || 0);
        const igst = safeNumber(inv.igstAmount || inv.igst || 0);

        agg.invoiceCount += 1;
        const invBase = deriveTaxableBase(inv.subtotal || inv.taxableAmount, inv.total || inv.totalAmount || inv.amount, cgst, sgst, igst);

        // Invoices reduce the aggregates (e.g., output tax collected reduces net tax payable in this context)
        agg.taxableAmount -= invBase;
        agg.cgst -= cgst;
        agg.sgst -= sgst;
        agg.igst -= igst;

        entries.push({
          type: "Tax Invoice",
          number: inv.invoiceNumber || inv.id,
          date: date,
          cgst: -cgst,
          sgst: -sgst,
          igst: -igst,
        });
      });

      // Process expenses and their vouchers (as positive amounts)
      expenseDocs.forEach((doc) => {
        const data = doc || {};
        // If document contains vouchers array, add each voucher
        if (Array.isArray(data.vouchers) && data.vouchers.length > 0) {
          data.vouchers.forEach((v, idx) => {
            const date = v.date || data.date || null;
            if (startDate || endDate) {
              if (!inRange(date)) return;
            }
            const cgst = safeNumber(v.cgst || v.cgstAmount || 0);
            const sgst = safeNumber(v.sgst || v.sgstAmount || 0);
            const igst = safeNumber(v.igst || v.igstAmount || 0);

            const voucherBase = deriveTaxableBase(v.taxableAmount || v.amount, v.totalWithGST || v.totalAmount, cgst, sgst, igst);
            agg.expenseCount += 1;
            agg.taxableAmount += voucherBase;
            agg.cgst += cgst;
            agg.sgst += sgst;
            agg.igst += igst;

            entries.push({
              type: "Expense",
              number: v.voucherId || `${doc.id}_voucher_${idx}`,
              date: date,
              cgst: cgst,
              sgst: sgst,
              igst: igst,
            });
          });
        }

        // Add parent expense if it's not a voucher and has amount
        if (data.type !== 'voucher' && (data.amount || data.totalWithGST)) {
          const date = data.date || data.createdOn || null;
          if (startDate || endDate) {
            if (!inRange(date)) return;
          }
          const cgst = safeNumber(data.cgst || data.cgstAmount || 0);
          const sgst = safeNumber(data.sgst || data.sgstAmount || 0);
          const igst = safeNumber(data.igst || data.igstAmount || 0);

          const parentBase = deriveTaxableBase(data.taxableAmount || data.amount, data.totalWithGST || data.totalAmount, cgst, sgst, igst);
          agg.expenseCount += 1;
          agg.taxableAmount += parentBase;
          agg.cgst += cgst;
          agg.sgst += sgst;
          agg.igst += igst;

          entries.push({
            type: "Expense",
            number: data.expenseNumber || data.id,
            date: date,
            cgst: cgst,
            sgst: sgst,
            igst: igst,
          });
        }
      });

      // If specific breakdowns are present, avoid double counting combined gst
      // Here we keep gst as-sum of any reported combined GST fields; it's acceptable to show both.

      // After processing all items compute totalAmount as sum of components (ensures consistency)
      // Sort entries by date ascending for listing
      entries.sort((a, b) => {
        const da = a.date ? new Date(a.date) : new Date(0);
        const db = b.date ? new Date(b.date) : new Date(0);
        return da - db;
      });

      // Recompute aggregated tax totals from the entries to avoid any mismatch
      const sumCgst = entries.reduce((s, e) => s + safeNumber(e.cgst), 0);
      const sumSgst = entries.reduce((s, e) => s + safeNumber(e.sgst), 0);
      const sumIgst = entries.reduce((s, e) => s + safeNumber(e.igst), 0);

      // If there is any material discrepancy, log a warning for debugging
      const eps = 0.005;
      if (Math.abs(sumCgst - agg.cgst) > eps || Math.abs(sumSgst - agg.sgst) > eps || Math.abs(sumIgst - agg.igst) > eps) {
        console.warn('TaxReport: aggregate mismatch detected, recomputing from entries', {
          aggBefore: { cgst: agg.cgst, sgst: agg.sgst, igst: agg.igst },
          sums: { sumCgst, sumSgst, sumIgst }
        });
      }

      // Use recomputed sums (rounded) as the canonical totals
      agg.cgst = Number(sumCgst.toFixed(2));
      agg.sgst = Number(sumSgst.toFixed(2));
      agg.igst = Number(sumIgst.toFixed(2));

      agg.totalAmount = Number((agg.cgst + agg.sgst + agg.igst).toFixed(2));

      setReport({ totals: agg, entries });
    } catch (e) {
      console.error("Error generating tax report:", e);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!report) return;
    const rows = [
      ["Summary", "Value"],
      ["Tax Invoices Count", report.totals.invoiceCount],
      ["Expenses Count", report.totals.expenseCount],
      ["CGST", report.totals.cgst.toFixed(2)],
      ["SGST", report.totals.sgst.toFixed(2)],
      ["IGST", report.totals.igst.toFixed(2)],
      ["Total Tax Due", report.totals.totalAmount.toFixed(2)],
      [],
      ["Sr#","Type","Number","Date","CGST","SGST","IGST"]
    ];

    // Add individual entries (use actual sign of each component so CSV matches totals)
    report.entries.forEach((e, idx) => {
      const cg = safeNumber(e.cgst);
      const sg = safeNumber(e.sgst);
      const ig = safeNumber(e.igst);
      const cgPrefix = cg < 0 ? '-' : '+';
      const sgPrefix = sg < 0 ? '-' : '+';
      const igPrefix = ig < 0 ? '-' : '+';
      rows.push([
        idx + 1,
        e.type,
        e.number,
        formatDateDMY(e.date) || '',
        `${cgPrefix}${Math.abs(cg).toFixed(2)}`,
        `${sgPrefix}${Math.abs(sg).toFixed(2)}`,
        `${igPrefix}${Math.abs(ig).toFixed(2)}`,
      ]);
    });
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tax-report-${startDate || 'start'}-${endDate || 'end'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 10, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Taxation Report
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => handleStartDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minAllowedDate, max: maxAllowedDate }}
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => handleEndDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minAllowedDate, max: maxAllowedDate }}
            sx={{ minWidth: 180 }}
          />
          <Button variant="contained" onClick={generate} disabled={loading}>
            {loading ? "Generating..." : "Generate Report"}
          </Button>
        </Box>

        {report ? (
          <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Paper sx={{ p: 1.25, minWidth: 160 }}>
                  <Typography variant="subtitle2">Tax Invoices</Typography>
                  <Typography variant="h6">{report.totals.invoiceCount}</Typography>
                </Paper>
                <Paper sx={{ p: 1.25, minWidth: 160 }}>
                  <Typography variant="subtitle2">Expenses</Typography>
                  <Typography variant="h6">{report.totals.expenseCount}</Typography>
                </Paper>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Paper sx={{ p: 1.25, minWidth: 140 }}>
                  <Typography variant="subtitle2">CGST</Typography>
                  <Typography variant="h6">₹ {report.totals.cgst.toFixed(2)}</Typography>
                </Paper>
                <Paper sx={{ p: 1.25, minWidth: 140 }}>
                  <Typography variant="subtitle2">SGST</Typography>
                  <Typography variant="h6">₹ {report.totals.sgst.toFixed(2)}</Typography>
                </Paper>
                <Paper sx={{ p: 1.25, minWidth: 140 }}>
                  <Typography variant="subtitle2">IGST</Typography>
                  <Typography variant="h6">₹ {report.totals.igst.toFixed(2)}</Typography>
                </Paper>
                <Paper sx={{ p: 1.25, minWidth: 180, backgroundColor: '#e3f2fd' }}>
                  <Typography variant="subtitle2">Total Tax Due</Typography>
                  <Typography variant="h6">₹ {report.totals.totalAmount.toFixed(2)}</Typography>
                </Paper>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" gutterBottom>Report</Typography>
              {report && (
                <Button onClick={downloadCSV} size="small" startIcon={<FileDownloadIcon />}>
                  CSV
                </Button>
              )}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sr#</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Id</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">CGST (₹)</TableCell>
                    <TableCell align="right">SGST (₹)</TableCell>
                    <TableCell align="right">IGST (₹)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.entries.map((e, idx) => (
                    <TableRow key={`${e.number}-${idx}`} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{e.type}</TableCell>
                      <TableCell>{e.number}</TableCell>
                      <TableCell>{e.date ? formatDateDMY(e.date) : 'N/A'}</TableCell>
                      <TableCell align="right">{e.cgst < 0 ? `- ₹ ${Math.abs(e.cgst).toFixed(2)}` : `+ ₹ ${Math.abs(e.cgst).toFixed(2)}`}</TableCell>
                      <TableCell align="right">{e.sgst < 0 ? `- ₹ ${Math.abs(e.sgst).toFixed(2)}` : `+ ₹ ${Math.abs(e.sgst).toFixed(2)}`}</TableCell>
                      <TableCell align="right">{e.igst < 0 ? `- ₹ ${Math.abs(e.igst).toFixed(2)}` : `+ ₹ ${Math.abs(e.igst).toFixed(2)}`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography color="text.secondary">No report generated yet.</Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
}
